/**
 * Community Service
 */
import { getApiClient } from '../apiClient';
import { COMMUNITY_ENDPOINTS, replacePathParams } from '../endpoints';
import type {
  GetCommunityGroupsResponse,
  GetCommunityGroupResponse,
  CreateCommunityGroupPayload,
  CreateCommunityGroupResponse,
  UpdateCommunityGroupPayload,
  UpdateCommunityGroupResponse,
  DeleteCommunityGroupResponse,
  GetCommunityGroupsParams,
  GetCommunityCommentsParams,
  GetCommunityCommentsResponse,
} from '../types/community.types';

export const communityService = {
  /**
   * Get all community groups
   * @param params - Optional query params (search, pagination)
   */
  getCommunityGroups: (params?: GetCommunityGroupsParams): Promise<GetCommunityGroupsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.page) {
      queryParams.append('page', String(params.page));
    }
    if (params?.limit) {
      queryParams.append('limit', String(params.limit));
    }
    if (params?.offset) {
      queryParams.append('offset', String(params.offset));
    }
    const url = queryParams.toString() 
      ? `${COMMUNITY_ENDPOINTS.getCommunityGroups}?${queryParams.toString()}`
      : COMMUNITY_ENDPOINTS.getCommunityGroups;
    return getApiClient().get(url);
  },

  /**
   * Get single community group by ID
   */
  getCommunityGroup: (id: string): Promise<GetCommunityGroupResponse> => {
    const url = replacePathParams(COMMUNITY_ENDPOINTS.getCommunityGroup, { id });
    return getApiClient().get(url);
  },

  /**
   * Create community group
   */
  createCommunityGroup: (body: CreateCommunityGroupPayload): Promise<CreateCommunityGroupResponse> => {
    return getApiClient().post(COMMUNITY_ENDPOINTS.createCommunityGroup, body);
  },

  /**
   * Update community group
   */
  updateCommunityGroup: (id: string, body: UpdateCommunityGroupPayload): Promise<UpdateCommunityGroupResponse> => {
    const url = replacePathParams(COMMUNITY_ENDPOINTS.updateCommunityGroup, { id });
    return getApiClient().put(url, body);
  },

  /**
   * Delete community group
   */
  deleteCommunityGroup: (id: string): Promise<DeleteCommunityGroupResponse> => {
    const url = replacePathParams(COMMUNITY_ENDPOINTS.deleteCommunityGroup, { id });
    return getApiClient().delete(url);
  },

  /**
   * Get community comments
   * @param params - Query params with communityId, page, and limit
   */
  getCommunityComments: (params: GetCommunityCommentsParams): Promise<GetCommunityCommentsResponse> => {
    const queryParams = new URLSearchParams();
    if (params.communityId) {
      queryParams.append('communityId', params.communityId);
    }
    if (params.page) {
      queryParams.append('page', String(params.page));
    }
    if (params.limit) {
      queryParams.append('limit', String(params.limit));
    }
    const url = `${COMMUNITY_ENDPOINTS.getCommunityComments}?${queryParams.toString()}`;
    return getApiClient().get(url);
  },
};

